import { useParams, useSearchParams, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import CheckInHeader from '../../components/check-in/CheckInHeader';
import CheckInNavBar from '../../components/check-in/CheckInNavBar';
import StrategyCard from '../../components/check-in/StrategyCard';
import StrategyListSkeleton from '../../components/check-in/StrategyListSkeleton';
import { useCheckInStrategies } from '../../hooks/useCheckIn';
import { getCategoryData, getSubEmotion } from '../../data/check-in';
import { containerVariants, itemVariants } from '../../lib/animations';

export default function CheckInStrategyListPage() {
  const { category } = useParams<{ category: string }>();
  const [searchParams] = useSearchParams();
  const subEmotionParam = searchParams.get('sub');

  const categoryData = category ? getCategoryData(category) : undefined;

  // Redirect if invalid category
  if (!categoryData) {
    return <Navigate to="/check-in/weather" replace />;
  }

  const subEmotion = subEmotionParam
    ? getSubEmotion(category!, subEmotionParam)
    : undefined;

  const { data: strategies, isLoading } = useCheckInStrategies(category!);

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ backgroundColor: '#C7EDEF' }}
    >
      {/* Gradient overlay */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'linear-gradient(to bottom, #58C5DA 195px, rgba(199,237,239,0) 598px)',
          top: '-7px',
        }}
      />

      <div className="relative z-10 flex flex-col">
        <CheckInHeader />
        <CheckInNavBar
          label={`BACK TO ${categoryData.name}`}
          to={`/check-in/${category}`}
        />

        <motion.div
          className="flex flex-col items-center px-6 mt-4 w-full max-w-[430px] mx-auto"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {/* Selected sub-emotion cloud */}
          {subEmotion && (
            <motion.div className="flex flex-col items-center" variants={itemVariants}>
              <img
                src={subEmotion.asset}
                alt={subEmotion.name}
                className="w-[160px] min-[414px]:w-[190px] md:w-[210px] max-w-full"
              />
              <h2
                className="text-[#1B2831] font-bold text-2xl sm:text-[28px] md:text-[32px] text-center mt-4"
                style={{ fontFamily: 'DM Sans', letterSpacing: '-1.28px' }}
              >
                {subEmotion.name}
              </h2>
            </motion.div>
          )}

          {/* Strategy list */}
          <motion.div
            className="w-full max-w-[382px] flex flex-col gap-2 mt-12 mb-8"
            variants={containerVariants}
          >
            {isLoading ? (
              <StrategyListSkeleton />
            ) : strategies && strategies.length > 0 ? (
              strategies.map((strategy) => (
                <motion.div key={strategy.id} variants={itemVariants}>
                  <StrategyCard
                    id={strategy.id}
                    title={strategy.title}
                    subtitle={strategy.subtitle}
                    iconImageUrl={strategy.iconImageUrl}
                    category={category}
                    subEmotionParam={subEmotionParam ?? undefined}
                  />
                </motion.div>
              ))
            ) : (
              <div className="text-center py-8">
                <p
                  className="text-base text-[#1B2831]"
                  style={{ fontFamily: 'DM Sans' }}
                >
                  No strategies available right now.
                </p>
              </div>
            )}

              {/* Section label */}
          <motion.p
            className="text-base text-[#1B2831] text-center mt-10 mb-4"
            style={{ fontFamily: 'DM Sans' }}
            variants={itemVariants}
          >
            Why this works: {categoryData.why}
          </motion.p>

          </motion.div>
        </motion.div>
      </div>
    </div>
  );
}
